import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ObjectCookie } from '../cookie';
import { DEVICES } from '../pagespeed.interface';
import { PageSpeedApiService, GetScoreRequest, SetScoreRequest } from '@vendasta/wsp-wp-manager';
export interface VitalMetricResult {
  [metricID: string]: number;
}

// the interface definition that stores the performance results
export interface PageSpeedScoreResult {
  score: number;
  passedRules: number;
  opportunities: number;
  updateDate: Date;
  // extend Results by adding vital metrics value
  vmResult: VitalMetricResult;
}

// the cookie keys for caching site performance results
export const DESKTOP_RESULTS_KEY = 'desktopResults';
export const MOBILE_RESULTS_KEY = 'mobileResults';

@Injectable()
export class StatsApiService {
  public desktopResultCookie: ObjectCookie = new ObjectCookie(DESKTOP_RESULTS_KEY);
  public mobileResultCookie: ObjectCookie = new ObjectCookie(MOBILE_RESULTS_KEY);

  // A audit score >= 0.9 means passing a rule while below means it is an opportunity for improvement
  // view https://developers.google.com/speed/docs/insights/v5/about performance score for explanation
  private pagespeedAuditThreshold = 0.9;

  constructor(
    private http: HttpClient,
    private alertService: SnackbarService,
    private translateService: TranslateService,
    private pageSpeedApiService: PageSpeedApiService,
  ) {}

  /**
   * Fetches the desktop results from the Page Speed Google API.
   */
  deviceFetch(siteUrl: string, device: string): Observable<PageSpeedScoreResult> {
    const reqData: GetScoreRequest = {
      domain: siteUrl,
      device: device,
      toApiJson: () => ({ domain: siteUrl, device: device }),
    };

    return this.pageSpeedApiService.getScore(reqData).pipe(
      map((result: any) => {
        const deviceResult = {
          score: 0,
          passedRules: 0,
          opportunities: 0,
          updateDate: new Date(),
          vmResult: {},
        } as PageSpeedScoreResult;

        if (result.success) {
          result = JSON.parse(result.response);
          deviceResult.score = result.lighthouseResult.categories?.performance?.score * 100;
          const auditsMap = result.lighthouseResult.audits;
          const auditRefs = result.lighthouseResult.categories?.performance?.auditRefs;
          for (const ref of auditRefs) {
            if (ref.group === 'load-opportunities') {
              if (auditsMap[ref.id].score >= this.pagespeedAuditThreshold) {
                deviceResult.passedRules += 1;
              } else {
                deviceResult.opportunities += 1;
              }
            } else if (ref.group === 'metrics') {
              const metricID = auditsMap[ref.id].id;
              deviceResult.vmResult[metricID] = auditsMap[ref.id].numericValue;
            }
          }

          if (device === DEVICES.DESKTOP) {
            this.desktopResultCookie.set(deviceResult);
          } else {
            this.mobileResultCookie.set(deviceResult);
          }
        }
        return deviceResult;
      }),
    );
  }

  /**
   * Clears the pagespeed cookies.
   */
  clear_cache(): void {
    this.desktopResultCookie.set('');
    this.mobileResultCookie.set('');
  }

  getDesktopCachedResults(): any {
    return this.desktopResultCookie.get();
  }

  getMobileCachedResults(): any {
    return this.mobileResultCookie.get();
  }

  setScore(siteId: string) {
    const mobileScore = this.getMobileCachedResults().score;
    const desktopScore = this.getDesktopCachedResults().score;
    const reqData: SetScoreRequest = {
      siteId: siteId,
      mobile: mobileScore,
      desktop: desktopScore,
      toApiJson: () => ({ siteId: siteId, mobile: mobileScore, desktop: desktopScore }),
    };

    this.pageSpeedApiService.setScore(reqData).subscribe((result: any) => {
      console.log(result);
    });
  }

  /**
   *
   * @param scoreAge: time eclipses in milliseconds
   */
  getScoreAgeText(scoreAge: number): string {
    if (scoreAge < 10000) {
      return this.translateService.instant('ANALYTICS.SPEED_SCORE_MODAL.SUBTITLES.REFRESHED');
    }

    const prefix = this.translateService.instant('ANALYTICS.UPDATE_TOOLTIP.UPDATED');
    let value: number;
    let unit: string;
    if (scoreAge < 60000) {
      value = scoreAge / 1000;
      unit = this.translateService.instant('ANALYTICS.UPDATE_TOOLTIP.SECONDS_AGO');
    } else if (scoreAge >= 60000 && scoreAge < 3600000) {
      value = scoreAge / 60000;
      unit = this.translateService.instant('ANALYTICS.UPDATE_TOOLTIP.MINUTES_AGO');
    } else if (scoreAge >= 3600000 && scoreAge < 86400000) {
      value = scoreAge / 3600000;
      unit = this.translateService.instant('ANALYTICS.UPDATE_TOOLTIP.HOURS_AGO');
    } else {
      value = scoreAge / 86400000;
      unit = this.translateService.instant('ANALYTICS.UPDATE_TOOLTIP.DAYS_AGO');
    }

    return prefix + ' ' + Math.ceil(value) + ' ' + unit;
  }
}
